import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { Check, FileText, Link2, Loader2, Music, X } from "lucide-react";
import { libraryGlowButtonClass } from "@/features/library/lib/library-ui";
import { cn, getFileUrl } from "@/lib/utils";
import { pickerCountBadgeClass, pickerPrimaryButtonClass } from "../lib/picker-ui";
import { useFilePickerModal, useFilePickerSelection, useFilePickerStore } from "../store/file-picker-store";
import type { SelectedFile } from "../types/file-picker-types";

const MAX_POSTERS = 6;
const MOBILE_POSTERS = 2;

const SelectedPoster = ({ file }: { file: SelectedFile }) => {
  const url = file.thumbnail || getFileUrl(file.url) || "";
  if (file.type.startsWith("image/") || (file.type === "url" && url)) {
    return <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />;
  }
  if (file.type.startsWith("video/")) {
    return (
      <video
        src={`${url}#t=0.1`}
        muted
        playsInline
        preload="metadata"
        className="pointer-events-none absolute inset-0 w-full h-full object-cover"
      />
    );
  }
  const Icon = file.type === "url" ? Link2 : file.type.startsWith("audio/") ? Music : FileText;
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-violet-100 to-sky-100 dark:from-violet-950 dark:to-sky-950">
      <Icon className="w-4 h-4 text-violet-600 dark:text-violet-300" />
    </div>
  );
};

/** Alt çubuk: seçilenlerin posterleri, limit göstergesi, iptal ve onay. */
export const PickerSelectionTray = () => {
  const { t } = useTranslation();
  const { closePicker, confirmSelection } = useFilePickerModal();
  const { selectedFiles, removeFile, maxSelection } = useFilePickerSelection();
  const uploadingCount = useFilePickerStore(
    (state) => state.uploads.filter((upload) => upload.status === "uploading").length
  );

  const isMulti = maxSelection > 1;
  const visible = selectedFiles.slice(0, MAX_POSTERS);
  const hiddenCount = selectedFiles.length - visible.length;

  return (
    <div className="shrink-0 border-t border-black/[0.06] dark:border-white/[0.08] bg-bg-primary/80 dark:bg-gray-950/70 backdrop-blur-xl px-3 sm:px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {selectedFiles.length === 0 ? (
            <p className="truncate text-xs sm:text-sm text-text-tertiary dark:text-gray-400">
              {uploadingCount > 0
                ? t("filePicker.tray.uploading", { count: uploadingCount })
                : isMulti
                  ? t("filePicker.tray.hintMulti", { count: maxSelection })
                  : t("filePicker.tray.hintSingle")}
            </p>
          ) : (
            <>
              <ul className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                <AnimatePresence initial={false}>
                  {visible.map((file, index) => (
                    <motion.li
                      key={file.id}
                      layout
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.6 }}
                      transition={{ duration: 0.18 }}
                      className={cn(
                        "group relative h-10 w-10 shrink-0 overflow-hidden rounded-xl bg-bg-secondary dark:bg-gray-800 ring-1 ring-black/10 dark:ring-white/10",
                        index >= MOBILE_POSTERS && "hidden sm:block"
                      )}
                      title={file.name}
                    >
                      <SelectedPoster file={file} />
                      <button
                        type="button"
                        onClick={() => removeFile(file.id)}
                        aria-label={t("filePicker.tray.remove", { name: file.name })}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 [@media(hover:none)]:bg-black/30"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
              {(hiddenCount > 0 || selectedFiles.length > MOBILE_POSTERS) && (
                <span className="shrink-0 text-xs font-semibold tabular-nums text-text-tertiary dark:text-gray-400">
                  {selectedFiles.length > MOBILE_POSTERS && (
                    <span className="sm:hidden">+{selectedFiles.length - MOBILE_POSTERS}</span>
                  )}
                  {hiddenCount > 0 && <span className="hidden sm:inline">+{hiddenCount}</span>}
                </span>
              )}
              {uploadingCount > 0 && (
                <Loader2 className="w-4 h-4 shrink-0 animate-spin text-text-tertiary dark:text-gray-400" />
              )}
            </>
          )}

          {isMulti && (
            <span
              className={cn(
                "ml-auto shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums",
                selectedFiles.length >= maxSelection
                  ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                  : "bg-black/[0.05] dark:bg-white/[0.08] text-text-secondary dark:text-gray-300"
              )}
            >
              {selectedFiles.length} / {maxSelection}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => closePicker("cancel")}
          className={cn(
            libraryGlowButtonClass,
            "h-10 shrink-0 px-3 sm:px-4 rounded-xl text-sm font-medium text-text-secondary dark:text-gray-300 hover:text-text-primary dark:hover:text-white"
          )}
        >
          {t("common.cancel")}
        </button>
        {isMulti && (
          <button
            type="button"
            onClick={confirmSelection}
            disabled={selectedFiles.length === 0}
            aria-label={t("common.confirm")}
            className={cn(pickerPrimaryButtonClass, "h-10 shrink-0 px-3 sm:px-4 text-sm")}
          >
            <Check className="w-4 h-4" />
            <span className="hidden sm:inline">{t("common.confirm")}</span>
            {selectedFiles.length > 0 && (
              <motion.span
                key={selectedFiles.length}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 26 }}
                className={pickerCountBadgeClass}
              >
                {selectedFiles.length}
              </motion.span>
            )}
          </button>
        )}
      </div>
    </div>
  );
};
